import { ReactNode } from 'react';
import { Header } from '@/components/Header';
import { PageShell } from '@/components/layout/PageShell';
import { SEO } from '@/components/SEO';
import { cn } from '@/lib/utils';

interface InventoryPageShellProps {
  /** SEO title override (defaults to "Inventory | CardSimp"). */
  seoTitle?: string;
  /** SEO description override. */
  seoDescription?: string;
  /** Screen-reader-only H1 for the page. */
  srHeading?: string;
  /** Desktop-only fixed toolbar row rendered under the app header. */
  desktopToolbar: ReactNode;
  /** Desktop-only left filter sidebar (lg+). */
  desktopSidebar: ReactNode;
  /** Mobile-only sticky toolbar rendered at the top of the scroll region. */
  mobileToolbar: ReactNode;
  /** Main scrollable content (grid/list/empty state). */
  children: ReactNode;
  /** Overlays / dialogs / selection action bars mounted outside the scroll region. */
  overlays?: ReactNode;
  /** Callback receiving the main scroll element (for virtualization parents). */
  onScrollElementChange?: (el: HTMLElement | null) => void;
  /** If true, drop the flex/scroll wrapper — used to render list view without extra padding. */
  contentPadding?: 'default' | 'list';
}

/**
 * Shared layout scaffold for inventory-style pages (Inventory, future
 * Menus / Wantlist admin surfaces). Owns the PageShell, header, desktop
 * fixed toolbar row, desktop filter sidebar, mobile sticky toolbar, and
 * the main scroll container. Callers supply slot content + overlays.
 */
export function InventoryPageShell({
  seoTitle = 'Inventory | CardSimp',
  seoDescription = 'Manage your TCG collection inventory. Track cards, set prices, and list items for sale on CardSimp.',
  srHeading = 'My Inventory',
  desktopToolbar,
  desktopSidebar,
  mobileToolbar,
  children,
  overlays,
  onScrollElementChange,
  contentPadding = 'default',
}: InventoryPageShellProps) {
  return (
    <PageShell scroll="none" className="relative">
      <SEO title={seoTitle} description={seoDescription} noindex />
      <h1 className="sr-only">{srHeading}</h1>
      <Header hideOnMobile />

      {/* Desktop fixed toolbar row — pinned under the app header, right of sidebar. */}
      <div
        className="hidden lg:flex fixed z-[25] h-14 items-center gap-3 bg-chrome shadow-[0_4px_12px_-2px_rgba(0,0,0,0.3)] border-b border-white/[0.08]"
        style={{ top: '70px', left: '256px', right: 0, paddingLeft: '24px', paddingRight: '24px' }}
      >
        {desktopToolbar}
      </div>

      {/* Main layout: desktop sidebar + scrollable content. */}
      <div className="flex items-stretch relative flex-1 min-h-0">
        <div className="hidden lg:block h-full w-64">
          <div className="h-full">{desktopSidebar}</div>
        </div>

        <div
          ref={(el) => onScrollElementChange?.(el)}
          className={cn(
            'flex-1 main-content-scroll overflow-y-auto',
            contentPadding === 'list'
              ? 'flex flex-col min-h-0 pb-3 pb-bottom-nav'
              : 'pb-12 pb-bottom-nav',
          )}
        >
          <div
            className="lg:hidden sticky z-[26] bg-chrome border-b border-white/[0.08] px-3 py-2.5 w-full overflow-x-hidden"
            style={{ top: 0, paddingTop: 'calc(env(safe-area-inset-top) + 10px)' }}
          >
            {mobileToolbar}
          </div>

          {/* Spacer for the fixed desktop toolbar (h-14). */}
          <div className="hidden lg:block h-14" />

          {children}
        </div>
      </div>

      {overlays}
    </PageShell>
  );
}
